/* eslint-disable react/prop-types */
import { Navigate, createBrowserRouter, RouterProvider, Outlet } from 'react-router-dom';
import SearchProvider from './Components/Organisms/SearchProvider';
import { ROUTES, DRAWER_STACK, DIALOG_TYPES, FEATURE_FLAGS } from './Utils/constants';
import AuthProvider from './Components/Organisms/AuthProvider/AuthProvider';
import { EventDispatcherProvider } from 'Components/Organisms/EventDispatcherContext';
import CallbackGoogle from './Pages/Callbacks/Google';
import TrackingProvider from 'Components/Organisms/TrackingProvider';
import ErrorsProvider from 'Components/Organisms/ErrorsProvider';
import UserProvider from 'Components/Organisms/UserProvider';
import AppLayout from 'Components/Templates/AppLayout/AppLayout';
import GuardPublic from 'Components/Templates/routing/GuardPublic';
import GuardPrivate from 'Components/Templates/routing/GuardPrivate';
import GuardOnboarding from 'Components/Templates/routing/GuardOnboarding';
import GuardWorkspace from 'Components/Templates/routing/GuardWorkspace';
import GuardFeatureFlag from 'Components/Templates/routing/GuardFeatureFlag/GuardFeatureFlag';
import NavigateWithSearch from 'Components/Templates/routing/NavigateWithSearch/NavigateWithSearch';
import { ErrorBoundary } from '@sentry/react';
import ErrorBoundaryRoute, { ErrorFallback } from 'Components/Organisms/ErrorBoundary';
import SnackbarProvider from 'Components/Molecules/SnackbarProvider/SnackbarProvider';
import { SlotsProvider } from 'nano-slots';
import useCheckLastAppVersion from 'Hooks/useCheckLastAppVersion';
import GlobalFloatingProvider from 'Components/Organisms/GlobalFloatingProvider/GlobalFloatingProvider';
import { getPage, registerChunkReloadListener } from 'Utils/loadPage';
import AppSkeleton from 'Components/Molecules/AppSkeleton/AppSkeleton';
import ContextualHelpProvider from 'Components/Organisms/ContextualHelpProvider/ContextualHelpProvider';
import DrawerStackProvider from 'Components/Organisms/DrawerStackProvider/DrawerStackProvider';
import ModalStackProvider from 'Components/Organisms/ModalStackProvider/ModalStackProvider';
import ProviderConnectionsProvider from 'Components/Organisms/ProviderConnectionsProvider/ProviderConnectionsProvider';
import SyncProvider from 'Components/Organisms/SyncProvider/SyncProvider';
import AISuggestionsDrawer from 'Components/Organisms/AISuggestionsDrawer/AISuggestionsDrawer';
import ContactEditorDrawer from 'Components/Organisms/ContactEditorDrawer/ContactEditorDrawer';
import ContactMergeDrawer from 'Components/Organisms/ContactMergeDrawer/ContactMergeDrawer';
import TransactionEditorDrawer from 'Components/Organisms/TransactionEditorDrawer/TransactionEditorDrawer';
import TransactionDeleteDrawer from 'Components/Organisms/TransactionDeleteDrawer/TransactionDeleteDrawer';
import TagEditorDrawer from 'Components/Organisms/TagEditorDrawer/TagEditorDrawer';
import SubTagEditorDrawer from 'Components/Organisms/SubTagEditorDrawer/SubTagEditorDrawer';
import TransactionTagsEditorDrawer from 'Components/Organisms/TransactionTagsEditorDrawer/TransactionTagsEditorDrawer';
import TagsSelectorDrawer from 'Components/Organisms/TagsSelectorDrawer/TagsSelectorDrawer';
import InvoiceQuickEditorDrawer from 'Components/Organisms/InvoiceQuickEditorDrawer/InvoiceQuickEditorDrawer';
import InvoiceDeleteDrawer from 'Components/Organisms/InvoiceDeleteDrawer/InvoiceDeleteDrawer';
import InvoiceSerieEditor from 'Components/Organisms/InvoiceSerieEditor/InvoiceSerieEditor';
import ConfirmationDrawer from 'Components/Organisms/ConfirmationDrawer/ConfirmationDrawer';
import InvoicePaymentMethodEditor from 'Components/Organisms/InvoicePaymentMethodEditor/InvoicePaymentMethodEditor';
import InvoicesFiltersProvider from './Pages/Invoices/InvoicesFiltersProvider';
import InvoicesInvoicingConfigDrawer from './Pages/Invoices/InvoicesInvoicingConfigDrawer';
import InvoiceSendDrawer from './Components/Organisms/InvoiceSendDrawer/InvoiceSendDrawer.jsx';
import DeleteItemDrawer from './Components/Organisms/DeleteItemDrawer/DeleteItemDrawer.jsx';
import InvoicesInvoicingTaxAuthoritiesDrawer from 'Pages/Invoices/InvoicesInvoicingTaxAuthoritiesDrawer';
import InvoicesInboxDrawer from 'Pages/Invoices/InvoicesInboxDrawer';
import InvoiceValidationWithReconciliationDrawer from 'Components/Organisms/InvoiceValidationDrawer/InvoiceValidationWithReconciliationDrawer';
import InvoiceValidationDrawer from 'Components/Organisms/InvoiceValidationDrawer/InvoiceValidationDrawer';
import SettingsStorageProvider from 'Components/Organisms/UserStorageProvider/UserStorageProvider';
import DialogProvider from 'Components/Organisms/DialogProvider/DialogProvider';
import DeleteConfirmDialogContent from 'Components/Molecules/DeleteConfirmDialog/DeleteConfirmDialogContent';
import ConfirmDialogContent from 'Components/Molecules/ConfirmDialog/ConfirmDialogContent';
import FirstStepsProvider from 'Components/Organisms/FirstStepsProvider/FirstStepsProvider';
import InvoicesInvoicingPaymentMethodsDrawer from 'Pages/Invoices/InvoicesInvoicingPaymentMethodsDrawer';
import TaxesCellDetailDrawer from 'Pages/Taxes/TaxesCellDetailDrawer';
import ForecastValueDrawer from 'Pages/Forecasts/ForecastValue';
import ForecastValueGoalDrawer from 'Pages/Forecasts/ForecastValueGoal';
import ForecastValueIgnoredDrawer from 'Pages/Forecasts/ForecastValueIgnored';
import CreateTransactionDrawer from 'Components/Organisms/CreateTransactionDrawer/CreateTransactionDrawer';
import CreateTransactionDrawerAddProvider from 'Components/Organisms/CreateTransactionDrawer/CreateTransactionDrawerAddProvider';
import CreateTransactionImportCSVDrawer from 'Components/Organisms/CreateTransactionImportCSVDrawer/CreateTransactionImportCSVDrawer';
import ContactsImportCSVDrawer from 'Components/Organisms/ContactsImportCSVDrawer/ContactsImportCSVDrawer';
import InvoiceCancelDrawer from 'Components/Organisms/InvoiceCancelDrawer/InvoiceCancelDrawer';
import InvoicesExportDrawer from 'Components/Organisms/InvoicesExportDrawer/InvoicesExportDrawer';
import ContactsExportDrawer from 'Components/Organisms/ContactsExportDrawer/ContactsExportDrawer';
import InvoicesInvoicingTaxAuthorityConfigDrawer from 'Pages/Invoices/InvoicesInvoicingTaxAuthorityConfigDrawer';
import InvoicesInvoicingSeriesDrawer from 'Pages/Invoices/InvoicesInvoicingSeriesDrawer';
import InvoicesInvoicingScanDrawer from 'Pages/Invoices/InvoicesInvoicingScanDrawer';
import InvoicesInvoicingDefaultValuesDrawer from 'Pages/Invoices/InvoicesInvoicingDefaultValuesDrawer';
import InvoicesInvoicingReferencesDrawer from 'Pages/Invoices/InvoicesInvoicingReferencesDrawer';
import ReferenceEditorDrawer from 'Pages/Invoices/InvoicesInvoicingReferenceEditorDrawer';
import InvoicesTaxAuthorityConfigCreateDrawer from 'Pages/Invoices/InvoicesTaxAuthorityConfigCreateDrawer';
import InvoicesTaxAuthorityConfigEditDrawer from 'Pages/Invoices/InvoicesTaxAuthorityConfigEditDrawer';

const AppLayoutWithProviders = () => (
    <DialogProvider
        sharedDialogs={[
            [DIALOG_TYPES.DELETE_CONFIRMATION, DeleteConfirmDialogContent],
            [DIALOG_TYPES.CONFIRMATION, ConfirmDialogContent],
        ]}
    >
        <SettingsStorageProvider>
            <DrawerStackProvider
                sharedDrawers={[
                    [DRAWER_STACK.CONTACT_EDITOR, ContactEditorDrawer],
                    [DRAWER_STACK.IA_SUGGESTIONS, AISuggestionsDrawer],
                    [DRAWER_STACK.CONTACT_MERGE, ContactMergeDrawer],
                    [DRAWER_STACK.TRANSACTION_EDITOR, TransactionEditorDrawer],
                    [DRAWER_STACK.TRANSACTION_DELETE, TransactionDeleteDrawer],
                    [DRAWER_STACK.TAG_EDITOR, TagEditorDrawer],
                    [DRAWER_STACK.SUBTAG_EDITOR, SubTagEditorDrawer],
                    [DRAWER_STACK.TRANSACTION_TAGS_EDITOR, TransactionTagsEditorDrawer],
                    [DRAWER_STACK.TAGS_SELECTOR, TagsSelectorDrawer],
                    [DRAWER_STACK.INVOICE_QUICK_EDITOR, InvoiceQuickEditorDrawer],
                    [DRAWER_STACK.INVOICE_SEND, InvoiceSendDrawer],
                    [DRAWER_STACK.INVOICE_DELETE, InvoiceDeleteDrawer],
                    [DRAWER_STACK.INVOICE_SERIES_EDITOR, InvoiceSerieEditor],
                    [DRAWER_STACK.INVOICE_PAYMENT_METHODS_EDITOR, InvoicePaymentMethodEditor],
                    [DRAWER_STACK.INVOICE_INVOICING_CONFIG, InvoicesInvoicingConfigDrawer],
                    [DRAWER_STACK.CONFIRMATION_DIALOG, ConfirmationDrawer],
                    [DRAWER_STACK.DELETE_ITEM, DeleteItemDrawer],
                    [DRAWER_STACK.INVOICE_INVOICING_TAX_AUTHORITIES, InvoicesInvoicingTaxAuthoritiesDrawer],
                    [DRAWER_STACK.INVOICE_INBOX, InvoicesInboxDrawer],
                    [DRAWER_STACK.INVOICE_VALIDATION_WITH_RECONCILIATION, InvoiceValidationWithReconciliationDrawer],
                    [DRAWER_STACK.INVOICE_VALIDATION, InvoiceValidationDrawer],
                    [DRAWER_STACK.INVOICE_INVOICING_PAYMENT_METHODS, InvoicesInvoicingPaymentMethodsDrawer],
                    [DRAWER_STACK.TAXES_CELL_DETAIL, TaxesCellDetailDrawer],
                    [DRAWER_STACK.FORECAST_VALUE, ForecastValueDrawer],
                    [DRAWER_STACK.FORECAST_VALUE_GOAL, ForecastValueGoalDrawer],
                    [DRAWER_STACK.FORECAST_VALUE_IGNORED, ForecastValueIgnoredDrawer],
                    [DRAWER_STACK.CREATE_TRANSACTION, CreateTransactionDrawer],
                    [DRAWER_STACK.ADD_MANUAL_PROVIDER, CreateTransactionDrawerAddProvider],
                    [DRAWER_STACK.IMPORT_TRANSACTIONS_CSV, CreateTransactionImportCSVDrawer],
                    [DRAWER_STACK.IMPORT_CONTACTS_CSV, ContactsImportCSVDrawer],
                    [DRAWER_STACK.INVOICE_CANCEL, InvoiceCancelDrawer],
                    [DRAWER_STACK.INVOICE_EXPORT, InvoicesExportDrawer],
                    [DRAWER_STACK.CONTACT_EXPORT, ContactsExportDrawer],
                    [DRAWER_STACK.INVOICE_TAX_AUTHORITY_CONFIG, InvoicesInvoicingTaxAuthorityConfigDrawer],
                    [DRAWER_STACK.INVOICE_INVOICING_SERIES, InvoicesInvoicingSeriesDrawer],
                    [DRAWER_STACK.INVOICE_INVOICING_SCANNING, InvoicesInvoicingScanDrawer],
                    [DRAWER_STACK.INVOICE_INVOICING_DEFAULT_VALUES, InvoicesInvoicingDefaultValuesDrawer],
                    [DRAWER_STACK.INVOICE_INVOICING_REFERENCES, InvoicesInvoicingReferencesDrawer],
                    [DRAWER_STACK.REFERENCES_EDITOR, ReferenceEditorDrawer],
                    [DRAWER_STACK.INVOICE_TAX_AUTHORITY_CONFIG_CREATE, InvoicesTaxAuthorityConfigCreateDrawer],
                    [DRAWER_STACK.INVOICE_TAX_AUTHORITY_CONFIG_EDIT, InvoicesTaxAuthorityConfigEditDrawer],
                ]}
            >
                <ModalStackProvider>
                    <FirstStepsProvider>
                        <ProviderConnectionsProvider>
                            <SyncProvider>
                                <AppLayout />
                            </SyncProvider>
                        </ProviderConnectionsProvider>
                    </FirstStepsProvider>
                </ModalStackProvider>
            </DrawerStackProvider>
        </SettingsStorageProvider>
    </DialogProvider>
);

const ProvidersRoot = () => (
    <EventDispatcherProvider>
        <GlobalFloatingProvider>
            <ErrorsProvider>
                <SnackbarProvider>
                    <UserProvider>
                        <TrackingProvider>
                            <AuthProvider>
                                <ContextualHelpProvider>
                                    <SearchProvider>
                                        <InvoicesFiltersProvider>
                                            <SlotsProvider>
                                                <Outlet />
                                            </SlotsProvider>
                                        </InvoicesFiltersProvider>
                                    </SearchProvider>
                                </ContextualHelpProvider>
                            </AuthProvider>
                        </TrackingProvider>
                    </UserProvider>
                </SnackbarProvider>
            </ErrorsProvider>
        </GlobalFloatingProvider>
    </EventDispatcherProvider>
);

registerChunkReloadListener();

const router = createBrowserRouter([
    {
        element: <ProvidersRoot />,
        errorElement: <ErrorBoundaryRoute />,
        children: [
            {
                // public routes
                element: <GuardPublic />,
                children: [
                    {
                        path: ROUTES.signIn,
                        lazy: getPage(() => import('./Pages/AuthSignIn')),
                    },
                    {
                        path: ROUTES.signInTest,
                        lazy: getPage(() => import('./Pages/AuthSignInTest')),
                    },
                    {
                        path: ROUTES.signUp,
                        lazy: getPage(() => import('./Pages/AuthSignUp')),
                    },
                    {
                        path: ROUTES.signUpEmailConfirm,
                        lazy: getPage(() => import('./Pages/AuthSignUpEmailConfirm')),
                    },
                    {
                        path: ROUTES.forgotPassword,
                        lazy: getPage(() => import('./Pages/AuthForgotPassword')),
                    },
                    {
                        path: ROUTES.guestSignUp,
                        lazy: getPage(() => import('./Pages/AuthGuestSignUp')),
                    },
                    {
                        path: ROUTES.callbackGoogle,
                        element: <CallbackGoogle />,
                    },
                    {
                        path: '*',
                        element: <Navigate to={ROUTES.signIn} replace />,
                    },
                ],
            },
            {
                // private routes
                element: <GuardPrivate />,
                children: [
                    {
                        element: <AppLayoutWithProviders />,
                        children: [
                            // {
                            //     path: ROUTES.reports,
                            //     lazy: getPage(() => import('./Pages/Reports/ReportsConnections')),
                            // },
                            {
                                path: ROUTES.alerts,
                                lazy: getPage(() => import('./Pages/Alerts/AlertsConnections')),
                            },
                            {
                                path: ROUTES.automations,
                                lazy: getPage(() => import('./Pages/Automations/Automations')),
                            },
                            {
                                path: ROUTES.dashboards,
                                lazy: getPage(() => import('./Pages/Dashboards/DashboardsRoot')),
                                children: [
                                    {
                                        path: `${ROUTES.dashboards}:dashboardId`,
                                        lazy: getPage(() => import('./Pages/Dashboards/Dashboard')),
                                    },
                                ],
                            },
                            {
                                path: ROUTES.categories,
                                lazy: getPage(() => import('./Pages/Categories/CategoriesRoot')),
                                children: [
                                    {
                                        path: `${ROUTES.categories}:taxonomyId`,
                                        lazy: getPage(() => import('./Pages/Categories/Categories')),
                                    },
                                ],
                            },
                            {
                                path: `${ROUTES.providers}/:view?`,
                                lazy: getPage(() => import('./Pages/Providers/ProviderConnections')),
                            },
                            {
                                path: ROUTES.integrations,
                                lazy: getPage(() => import('./Pages/Integrations/Integrations')),
                            },
                            {
                                path: `${ROUTES.settings}/:view?`,
                                lazy: getPage(() => import('./Pages/Settings/Settings')),
                            },
                            {
                                path: ROUTES.search,
                                lazy: getPage(() => import('./Pages/Search/Search')),
                            },
                            {
                                path: ROUTES.invoices,
                                lazy: getPage(() => import('./Pages/Invoices/InvoicesRoot')),
                                children: [
                                    {
                                        path: ROUTES.invoices,
                                        lazy: getPage(() => import('./Pages/Invoices/Invoices')),
                                    },
                                    {
                                        path: `${ROUTES.invoicesEdit}/:invoiceId`,
                                        lazy: getPage(() => import('./Pages/Invoices/InvoicesEdit')),
                                    },
                                    {
                                        path: ROUTES.invoicesAdd,
                                        lazy: getPage(() => import('./Pages/Invoices/InvoicesAdd')),
                                    },
                                    {
                                        path: ROUTES.invoicesIssue,
                                        lazy: getPage(() => import('./Pages/Invoices/InvoicesIssue')),
                                    },
                                    {
                                        element: (
                                            <GuardFeatureFlag
                                                flag={FEATURE_FLAGS.INVOICE_REMINDERS}
                                                redirectTo={ROUTES.invoices}
                                            />
                                        ),
                                        children: [
                                            {
                                                path: ROUTES.invoicesReminders,
                                                lazy: getPage(
                                                    () => import('./Pages/InvoicesReminders/InvoicesReminders'),
                                                ),
                                            },
                                            {
                                                path: ROUTES.invoicesRemindersNew,
                                                lazy: getPage(
                                                    () => import('./Pages/InvoicesReminders/InvoicesRemindersCreate'),
                                                ),
                                            },
                                            {
                                                path: `${ROUTES.invoicesRemindersEdit}/:reminderId`,
                                                lazy: getPage(
                                                    () => import('./Pages/InvoicesReminders/InvoicesRemindersEdit'),
                                                ),
                                            },
                                        ],
                                    },
                                ],
                            },
                            {
                                path: ROUTES.contacts,
                                lazy: getPage(() => import('./Pages/Contacts/Contacts')),
                            },
                            {
                                path: ROUTES.profile,
                                lazy: getPage(() => import('./Pages/Profile/Profile')),
                            },
                            {
                                path: ROUTES.partners,
                                lazy: getPage(() => import('./Pages/Partners/Partners')),
                            },
                            {
                                path: ROUTES.forecasts,
                                lazy: getPage(() => import('./Pages/Forecasts/ForecastsRoot')),
                                children: [
                                    {
                                        path: `${ROUTES.forecasts}:forecastId`,
                                        lazy: getPage(() => import('./Pages/Forecasts/Forecast')),
                                    },
                                ],
                            },
                            {
                                path: ROUTES.recurrences,
                                lazy: getPage(() => import('./Pages/Recurrences/Recurrences')),
                            },
                            {
                                path: ROUTES.recurrencesTemplate,
                                lazy: getPage(() => import('./Pages/Recurrences/RecurrencesTemplate')),
                            },
                            {
                                path: ROUTES.calendar,
                                lazy: getPage(() => import('./Pages/Calendar/Calendar')),
                            },
                            {
                                path: ROUTES.reconciliation,
                                lazy: getPage(() => import('./Pages/Reconciliation/Reconciliation')),
                            },
                            {
                                path: ROUTES.tagging,
                                lazy: getPage(() => import('./Pages/Tagging/Tagging')),
                            },
                            {
                                path: ROUTES.scanInvoices,
                                lazy: getPage(() => import('./Pages/ScanInvoices/ScanInvoices')),
                            },
                            {
                                path: ROUTES.scanInvoicesValidateInvoice,
                                lazy: getPage(() => import('./Pages/ScanInvoices/ScanInvoicesValidation')),
                            },
                            {
                                path: ROUTES.taxes,
                                lazy: getPage(() => import('./Pages/Taxes/Taxes')),
                            },
                            {
                                path: ROUTES.firstSteps,
                                lazy: getPage(() => import('./Pages/FirstSteps/FirstSteps')),
                            },
                            {
                                path: ROUTES.firstStepsInvoicesUpload,
                                lazy: getPage(() => import('./Pages/FirstSteps/FirstStepsInvoicesUpload')),
                            },
                            {
                                path: ROUTES.firstStepsInvoicesIssue,
                                lazy: getPage(() => import('./Pages/FirstSteps/FirstStepsInvoicesIssue')),
                            },
                            {
                                path: ROUTES.firstStepsProvidersConnect,
                                lazy: getPage(() => import('./Pages/FirstSteps/FirstStepsProvidersConnect')),
                            },
                            {
                                path: ROUTES.archiveTransactions,
                                lazy: getPage(() => import('./Pages/ArchiveTransactions/ArchiveTransactions')),
                            },
                            {
                                // legacy URL: the page now lives under settings
                                path: ROUTES.versions,
                                element: <Navigate to={ROUTES.settingsVersions} replace />,
                            },
                            {
                                // legacy URL: the page now lives under banks, keep `?connector=` for the alert emails
                                path: ROUTES.settingsBanksHealth,
                                element: <NavigateWithSearch to={ROUTES.providersHealth} />,
                            },
                        ],
                    },
                    {
                        path: '*',
                        element: <Navigate to={ROUTES.dashboards} />,
                    },
                ],
            },
            {
                // onboarding routes
                element: <GuardOnboarding />,
                children: [
                    {
                        path: ROUTES.onboarding,
                        lazy: getPage(() => import('./Pages/Onboarding')),
                    },
                    {
                        path: '*',
                        element: <Navigate to={ROUTES.onboarding} />,
                    },
                ],
            },
            {
                // select workspace
                element: <GuardWorkspace />,
                children: [
                    {
                        path: ROUTES.selectWorkspace,
                        lazy: getPage(() => import('./Pages/SelectWorkspace')),
                    },
                    {
                        path: '*',
                        element: <Navigate to={ROUTES.selectWorkspace} />,
                    },
                ],
            },
        ],
    },
]);

const sharedRouter = createBrowserRouter([
    {
        errorElement: <ErrorBoundaryRoute />,
        children: [
            {
                path: '/reports/:id',
                lazy: getPage(() => import('./Pages/SharedReport/SharedReport')),
            },
            {
                path: '*',
                lazy: getPage(() => import('./Pages/SharedNotFound/SharedNotFound')),
            },
        ],
    },
]);

const isSharedSubdomain = window.location.hostname.startsWith('shared.');

export default function App() {
    useCheckLastAppVersion();

    return (
        <ErrorBoundary fallback={ErrorFallback}>
            <RouterProvider router={isSharedSubdomain ? sharedRouter : router} fallbackElement={<AppSkeleton />} />
        </ErrorBoundary>
    );
}
